/**
 * Google API HTTP Client
 *
 * Provides a robust HTTP client for Google APIs with:
 * - API key management
 * - Automatic retry with exponential backoff
 * - Request timeout handling
 * - Rate limit handling
 * - Proper error classification
 */

import { GeoApiError, GeoRateLimitError, GeoError } from '../../types/common'
import { getLogger } from '@hopdrive/sdk-core'

export interface GoogleClientConfig {
  /** Google API key */
  apiKey: string
  /** Request timeout in milliseconds (default: 10000) */
  timeout?: number
  /** Number of retry attempts (default: 3) */
  maxRetries?: number
  /** Base delay for exponential backoff in ms (default: 200) */
  baseDelayMs?: number
  /** Maximum delay between retries in ms (default: 5000) */
  maxDelayMs?: number
}

export interface RequestOptions extends Omit<RequestInit, 'signal'> {
  /** Request-specific timeout override */
  timeout?: number
  /** Whether to skip automatic retries */
  noRetry?: boolean
  /** Additional query parameters */
  queryParams?: Record<string, string | undefined>
}

/** HTTP status codes that should trigger a retry */
const RETRYABLE_STATUS_CODES = new Set([
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
])

/** Google API error response structure */
interface GoogleApiErrorResponse {
  error?: {
    code?: number
    message?: string
    status?: string
    details?: unknown[]
  }
}

export class GoogleClient {
  private readonly apiKey: string
  private readonly timeout: number
  private readonly maxRetries: number
  private readonly baseDelayMs: number
  private readonly maxDelayMs: number

  constructor(config: GoogleClientConfig) {
    if (!config.apiKey) {
      throw new Error('Google API key is required')
    }
    this.apiKey = config.apiKey
    this.timeout = config.timeout ?? 10000
    this.maxRetries = config.maxRetries ?? 3
    this.baseDelayMs = config.baseDelayMs ?? 200
    this.maxDelayMs = config.maxDelayMs ?? 5000
  }

  /**
   * Make a GET request to a Google API endpoint
   */
  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, { ...options, method: 'GET' })
  }

  /**
   * Make a POST request to a Google API endpoint
   */
  async post<T>(
    url: string,
    body: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })
  }

  /**
   * Make an HTTP request with retry logic
   */
  async request<T>(url: string, options?: RequestOptions): Promise<T> {
    const urlWithParams = this.buildUrl(url, options?.queryParams)
    const requestTimeout = options?.timeout ?? this.timeout
    const maxAttempts = options?.noRetry ? 1 : this.maxRetries + 1

    let lastError: Error | undefined

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const result = await this.executeRequest<T>(
          urlWithParams,
          options,
          requestTimeout
        )
        return result
      } catch (error) {
        lastError = error as Error

        // Don't retry if we've exhausted attempts or error isn't retryable
        if (attempt >= maxAttempts - 1 || !this.isRetryable(error)) {
          throw error
        }

        // Calculate delay with exponential backoff and jitter
        const delay = this.calculateBackoff(attempt)
        getLogger().warn('googleClient.retry', {
          attempt: attempt + 1,
          maxAttempts,
          delayMs: Math.round(delay),
          statusCode: lastError instanceof GeoApiError ? lastError.statusCode : undefined,
          errorMessage: lastError.message,
        })
        await this.sleep(delay)
      }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError ?? new GeoError('Request failed after all retries')
  }

  private async executeRequest<T>(
    url: string,
    options: RequestOptions | undefined,
    timeout: number
  ): Promise<T> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const { queryParams, timeout: _timeout, noRetry, ...fetchOptions } =
        options ?? {}

      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      })

      if (!response.ok) {
        await this.handleErrorResponse(response)
      }

      // Handle empty responses
      const text = await response.text()
      if (!text) {
        return {} as T
      }

      return JSON.parse(text) as T
    } catch (error) {
      if (error instanceof GeoError) {
        throw error
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new GeoApiError(`Request timeout after ${timeout}ms`, 408)
        }
        throw new GeoApiError(
          `Network error: ${error.message}`,
          0,
          { cause: error.message }
        )
      }

      throw new GeoError('Unknown error during request')
    } finally {
      clearTimeout(timeoutId)
    }
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    let errorData: GoogleApiErrorResponse | undefined

    try {
      const text = await response.text()
      if (text) {
        errorData = JSON.parse(text) as GoogleApiErrorResponse
      }
    } catch {
      // Ignore JSON parse errors
    }

    const errorMessage =
      errorData?.error?.message ?? response.statusText ?? 'Request failed'

    // Handle rate limiting
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After')
      const retryAfterMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : undefined
      throw new GeoRateLimitError(
        `Rate limit exceeded: ${errorMessage}`,
        retryAfterMs
      )
    }

    throw new GeoApiError(errorMessage, response.status, errorData)
  }

  private buildUrl(
    baseUrl: string,
    queryParams?: Record<string, string | undefined>
  ): string {
    const url = new URL(baseUrl)

    // Add API key
    url.searchParams.set('key', this.apiKey)

    // Add additional query params
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined) {
          url.searchParams.set(key, value)
        }
      }
    }

    return url.toString()
  }

  private isRetryable(error: unknown): boolean {
    if (error instanceof GeoRateLimitError) {
      return true
    }

    if (error instanceof GeoApiError) {
      return RETRYABLE_STATUS_CODES.has(error.statusCode)
    }

    // Retry network errors
    if (error instanceof Error) {
      return (
        error.name === 'AbortError' ||
        error.message.includes('network') ||
        error.message.includes('ECONNRESET') ||
        error.message.includes('ETIMEDOUT')
      )
    }

    return false
  }

  private calculateBackoff(attempt: number): number {
    // Exponential backoff: baseDelay * 2^attempt
    const exponentialDelay = this.baseDelayMs * Math.pow(2, attempt)

    // Add jitter (0-25% of delay)
    const jitter = Math.random() * exponentialDelay * 0.25

    // Cap at maxDelay
    return Math.min(exponentialDelay + jitter, this.maxDelayMs)
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}
